import React, {
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import styled from '@emotion/native';
import { useMutation } from '@apollo/client';
import { useWindowDimensions, ActivityIndicator } from 'react-native';
import { PaddingContainer, Subtitle, Title } from './styledComponents';
import ScrollableView from '@/shared/components/ScrollableView';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { GET_USER } from '@/shared/queries';
import { UPDATE_USER_ADDRESS } from '@/shared/mutations';
import {
  Country,
  MutationUpdateUserAddressArgs,
  MutationResult,
} from '@/shared/types/generated';
import { mapLocationTypeToEnum } from '@/components/editProfileForm/addressMappers';
import AddressForm, {
  AddressFormData,
} from '@/components/editProfileForm/Address';
import useStore from '@/stores/useStore';
import { green, red } from '@/shared/styles/colors';
import { BodyText } from '@/shared/components/StyledText';

interface AddressStepProps {
  onAddressValid: (isValid: boolean) => void;
}

export interface AddressStepHandle {
  saveAddress: () => Promise<boolean>;
}

const AddressStep = forwardRef<AddressStepHandle, AddressStepProps>(
  ({ onAddressValid }, ref) => {
    const { user, isLoadingUser } = useGetUser();
    const { width } = useWindowDimensions();
    const { setSnackbarState } = useStore();

    const [addressSaving, setAddressSaving] = useState(false);
    const [addressSaveError, setAddressSaveError] = useState<string | null>(
      null,
    );

    // Track the current form data
    const formDataRef = React.useRef<AddressFormData | null>(null);

    const [updateUserAddress] = useMutation<
      MutationResult<'updateUserAddress'>,
      MutationUpdateUserAddressArgs
    >(UPDATE_USER_ADDRESS, {
      refetchQueries: [{ query: GET_USER }],
    });

    // Save address - called by parent via ref
    const saveAddress = useCallback(async (): Promise<boolean> => {
      const formData = formDataRef.current;

      if (
        !formData ||
        !formData.city ||
        !formData.state ||
        !formData.addressLine1.trim() ||
        !formData.zipCode.trim()
      ) {
        setAddressSaveError('Please fill in all required fields.');
        return false;
      }

      const streetAddress = [formData.addressLine1, formData.addressLine2]
        .filter(Boolean)
        .join('\n');

      setAddressSaving(true);
      setAddressSaveError(null);

      try {
        await updateUserAddress({
          variables: {
            input: {
              streetAddress,
              city: formData.city,
              state: formData.state,
              zipCode: formData.zipCode,
              country: Country.Usa,
              locationType: mapLocationTypeToEnum(formData.locationType),
              accessDetails: formData.accessDetails || null,
            },
          },
        });
        setSnackbarState({
          open: true,
          message: 'Address saved successfully',
          severity: 'success',
        });
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to save address. Please try again.';
        setAddressSaveError(errorMessage);
        setSnackbarState({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        return false;
      } finally {
        setAddressSaving(false);
      }
    }, [updateUserAddress, setSnackbarState]);

    // Expose saveAddress method to parent via ref
    useImperativeHandle(ref, () => ({
      saveAddress,
    }));

    // Track form data changes
    const handleAddressChange = useCallback((formData: AddressFormData) => {
      formDataRef.current = formData;
      // Clear error when user starts editing again
      setAddressSaveError(null);
    }, []);

    if (isLoadingUser) {
      return (
        <Container>
          <LoadingContainer>
            <ActivityIndicator size="large" color={green} />
          </LoadingContainer>
        </Container>
      );
    }

    return (
      <Container>
        <Content
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            alignItems: 'center',
            width: width,
          }}
        >
          <PaddingContainer marginBottom={32}>
            <Title>Where Should We Meet?</Title>
            <Subtitle>
              Enter your address for the in-person styling session.
            </Subtitle>
          </PaddingContainer>

          <PaddingContainer>
            <AddressForm
              user={user}
              onAddressChange={handleAddressChange}
              onValidChange={onAddressValid}
              disabled={addressSaving}
            />
            {addressSaveError && <ErrorText>{addressSaveError}</ErrorText>}
            {addressSaving && (
              <SavingOverlay>
                <ActivityIndicator size="small" color={green} />
              </SavingOverlay>
            )}
          </PaddingContainer>
        </Content>
      </Container>
    );
  },
);

const Container = styled.View({
  flex: 1,
});

const Content = styled(ScrollableView)({
  flex: 1,
});

const LoadingContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const SavingOverlay = styled.View({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  justifyContent: 'center',
  alignItems: 'center',
});

const ErrorText = styled(BodyText)({
  color: red,
  fontSize: 14,
  marginBottom: 16,
});

AddressStep.displayName = 'AddressStep';

export default AddressStep;
